import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function Verify() {
  const { t } = useTranslation();
  const { verify } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [state, setState] = useState({ status: "loading", message: "" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState({ status: "error", message: t("verify.errNoToken") });
      return;
    }
    (async () => {
      try {
        await verify(token);
        setState({ status: "ok", message: t("verify.successMsg") });
        setTimeout(() => nav("/", { replace: true }), 1800);
      } catch (ex) {
        setState({
          status: "error",
          message:
            formatApiErrorDetail(ex.response?.data?.detail) ||
            t("verify.errFallback"),
        });
      }
    })();
  }, [token, verify, nav, t]);

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md animate-fade-in">
        <div className="mb-10 text-center">
          <div className="font-display text-5xl leading-none text-stone-900">progres.</div>
        </div>
        <div className="card-soft p-8 text-center" data-testid="verify-card">
          {state.status === "loading" && (
            <>
              <Loader2 className="w-8 h-8 text-stone-500 mx-auto animate-spin mb-4" />
              <h1 className="font-display text-2xl text-stone-900 mb-1">{t("verify.activating")}</h1>
              <p className="text-sm text-stone-500">{t("verify.checking")}</p>
            </>
          )}
          {state.status === "ok" && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-emerald-600 mx-auto flex items-center justify-center mb-5">
                <CheckCircle2 className="w-7 h-7 text-white" />
              </div>
              <h1 className="font-display text-2xl text-stone-900 mb-2">{t("verify.okTitle")}</h1>
              <p className="text-sm text-stone-500 mb-4">{state.message}</p>
              <p className="text-xs text-stone-500">{t("verify.redirecting")}</p>
            </>
          )}
          {state.status === "error" && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-[#EF4444] mx-auto flex items-center justify-center mb-5">
                <XCircle className="w-7 h-7 text-white" />
              </div>
              <h1 className="font-display text-2xl text-stone-900 mb-2">
                {t("verify.failedTitle")}
              </h1>
              <p className="text-sm text-stone-500 mb-6">{state.message}</p>
              <Link
                to="/login"
                data-testid="verify-back-to-login"
                className="btn-pill inline-flex"
              >
                {t("auth.backToLogin")}
              </Link>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
